/**
 * ANTLITZWERK product media controller — protected playback contract v8.1.0.
 *
 * PROTECTED CONTRACT — do not alter video selectors, ownership or lifecycle
 * without re-running the gallery regression suite. Horizon remains the visual
 * and navigation base; this controller alone owns product-video load, play,
 * pause, sound, viewport suspension and timestamp restoration. All image
 * typography, alt text, media ordering, native scrolling and zoom behaviour
 * remain outside the video lifecycle and must stay unchanged.
 *
 * AUDIO CONTRACT v8.1.0 — active, visible product videos request autoplay in
 * the visitor's desired state, which defaults to SOUND ON. Only a browser
 * NotAllowedError may trigger the immediate muted fallback. Never restore the
 * former "automatic entry is always muted" rule. The sound control must unmute
 * the already-running video directly, without reload, restart or lost position.
 */

const GALLERY_SELECTOR = 'body.page-type-product media-gallery[data-aw-product-gallery]';
const INLINE_SLIDE_SELECTOR =
  ':scope > slideshow-container > slideshow-slides > slideshow-slide[data-aw-media-index]';
const VIDEO_ITEM_SELECTOR =
  'slideshow-slide[data-aw-media-index], .media-gallery__grid > li[data-aw-media-index], .dialog-zoomed-gallery > li[data-aw-media-index]';
const VIDEO_ROOT_SELECTOR = '[data-aw-product-video]';
const SOUND_SELECTOR = '[data-aw-video-sound-toggle]';
const EXPAND_SELECTOR = '[data-aw-video-expand]';
const PLAY_SELECTOR = '[data-aw-video-play-toggle]';
const PROTECTED_CONTROL_SELECTOR = '[data-aw-control]';
const POINTER_INTENT_DISTANCE = 7;
const CLICK_SUPPRESSION_MS = 470;
const NAVIGATION_LOCK_MS = 170;
const WHEEL_THRESHOLD = 36;
const WHEEL_IDLE_MS = 190;
const PROGRESS_EDGE_INSET = 3.5;
const RENDER_NEIGHBOUR_RADIUS = 1;
const EDGE_SWIPE_MIN_DISTANCE = 42;
const EDGE_SWIPE_DOMINANCE = 1.16;
const EDGE_SCROLL_TOLERANCE = 4;
const RESUME_SEEK_EPSILON_SECONDS = 0.12;
const LOOP_RESTORE_GUARD_SECONDS = 0.35;
const GALLERY_CONTRACT_VERSION = '8.1.0';
const PLAY_PROMISE_TIMEOUT_MS = 8000;
const PLAYER_DISCOVERY_TIMEOUT_MS = 1600;
const TIME_MEMORY_INTERVAL_MS = 420;
const SCROLL_SETTLE_MS = 90;
const VIEWPORT_INSET_PX = 48;

/** @type {WeakMap<HTMLElement, GalleryState>} */
const galleryStates = new WeakMap();
/** @type {Set<GalleryState>} */
const activeStates = new Set();

// Deliberately page-scoped only: no localStorage/cookie and no added app.
/** @type {boolean} */
let pageSoundPreference = true;

const translations = {
  de: { status: 'Medium {current} von {total}', soundOn: 'Ton einschalten', soundOff: 'Ton ausschalten' },
  en: { status: 'Media {current} of {total}', soundOn: 'Turn sound on', soundOff: 'Turn sound off' },
  fr: { status: 'Média {current} sur {total}', soundOn: 'Activer le son', soundOff: 'Couper le son' },
};

/**
 * Lightweight gesture record used only when a native swipe pushes beyond the
 * first or last slide. It never prevents or drives ordinary scrolling.
 * @typedef {Object} EdgePointerState
 * @property {'pointer'|'touch'} source
 * @property {number} id
 * @property {number} startX
 * @property {number} startY
 * @property {number} lastX
 * @property {number} lastY
 * @property {number} startPosition
 * @property {boolean} cancelled
 */

/**
 * @typedef {Object} PlaybackMemory
 * @property {number} [time]
 * @property {boolean} [audible]
 */

/**
 * @typedef {Object} GalleryState
 * @property {HTMLElement} gallery
 * @property {HTMLElement} slideshow
 * @property {HTMLElement} scroller
 * @property {HTMLElement | null} zoomDialog
 * @property {AbortController} controller
 * @property {MutationObserver} observer
 * @property {number} activeIndex
 * @property {'inline'|'fullscreen'|'closing'} context
 * @property {number} activationToken
 * @property {number} mutationFrame
 * @property {number} suppressClickUntil
 * @property {number} lockUntil
 * @property {number | null} lockTimer
 * @property {EdgePointerState | null} edgePointer
 * @property {number} wheelTotal
 * @property {boolean} wheelLatched
 * @property {number | null} wheelTimer
 * @property {WeakSet<HTMLVideoElement>} boundVideos
 * @property {WeakSet<HTMLIFrameElement>} boundExternalVideos
 * @property {Map<number, PlaybackMemory>} videoMemory
 * @property {number} playbackSerial
 * @property {WeakMap<HTMLElement, number>} playbackTokens
 * @property {WeakMap<HTMLElement, boolean>} playbackIntent
 * @property {{index:number} | null} manualPause
 * @property {HTMLElement[]} inlineSlides
 * @property {number[]} inlineIndexes
 * @property {Map<number, HTMLElement>} slideByIndex
 * @property {{center:number,position:number}[]} inlineProgressGeometry
 * @property {number} inlineViewportWidth
 * @property {number | null} geometryFrame
 * @property {ResizeObserver | null} sizeObserver
 * @property {Set<HTMLElement>} readySlides
 * @property {HTMLElement | null} progress
 * @property {HTMLElement | null} progressTrack
 * @property {number} progressTrackWidth
 * @property {number | null} resizeFrame
 * @property {number | null} progressFrame
 * @property {number | null} progressFollowTimer
 * @property {boolean} pageVisible
 * @property {boolean} galleryVisible
 * @property {IntersectionObserver | null} viewportObserver
 * @property {number | null} visibilityFrame
 * @property {number | null} scrollSettleTimer
 * @property {WeakMap<HTMLElement, number>} pendingPlayback
 * @property {WeakMap<HTMLVideoElement, number>} timeMemoryAt
 * @property {boolean} contractWarningIssued
 */

function localeText() {
  const language = (document.documentElement.lang || 'de').toLowerCase().split('-')[0];
  return translations[language] || translations.de;
}

/** @param {number} value */
function pad(value) {
  return String(Math.max(1, Number(value) || 1)).padStart(2, '0');
}

/** @param {Event} event */
function stopEvent(event) {
  if (event.cancelable) event.preventDefault();
  event.stopPropagation();
  event.stopImmediatePropagation();
}

/** @param {Element | null | undefined} element */
function mediaIndex(element) {
  const direct = element?.closest?.('[data-aw-media-index]') || element;
  const value = Number(direct?.dataset?.awMediaIndex);
  return Number.isInteger(value) && value >= 0 ? value : 0;
}

/** @param {GalleryState} state */
function refreshInlineCache(state) {
  const slides = [...state.slideshow.querySelectorAll(INLINE_SLIDE_SELECTOR)]
    .filter((slide) => !slide.hasAttribute('hidden') || slide.hasAttribute('reveal'))
    .sort((a, b) => mediaIndex(a) - mediaIndex(b));
  state.inlineSlides = slides;
  state.inlineIndexes = slides.map(mediaIndex);
  state.slideByIndex = new Map(slides.map((slide) => [mediaIndex(slide), slide]));
  state.inlineProgressGeometry = [];
  state.inlineViewportWidth = 0;
}

/** @param {GalleryState} state */
function updateInlineGeometry(state) {
  if (!state.gallery.isConnected) return;
  updateProgressMetrics(state);

  const indexes = getAvailableIndexes(state);
  const positions = new Map(indexes.map((index, position) => [index, position]));
  const geometry = [];

  for (const slide of getInlineSlides(state)) {
    const position = positions.get(mediaIndex(slide));
    const width = slide.offsetWidth;
    if (!Number.isInteger(position) || width <= 0) continue;
    geometry.push({ center: slide.offsetLeft + width / 2, position });
  }

  geometry.sort((a, b) => a.center - b.center);
  state.inlineProgressGeometry = geometry;
  state.inlineViewportWidth = Math.max(1, state.scroller.clientWidth || 1);
}

/** @param {GalleryState} state */
function scheduleInlineGeometry(state) {
  if (state.geometryFrame != null) return;
  state.geometryFrame = requestAnimationFrame(() => {
    state.geometryFrame = null;
    updateInlineGeometry(state);
  });
}

/** @param {GalleryState} state */
function getInlineSlides(state) {
  if (!state.inlineSlides?.length) refreshInlineCache(state);
  return state.inlineSlides;
}

/** @param {GalleryState} state */
function getAvailableIndexes(state) {
  if (!state.inlineIndexes?.length) refreshInlineCache(state);
  return state.inlineIndexes;
}

/** @param {GalleryState} state @param {number} index */
function normalizeIndex(state, index) {
  const indexes = getAvailableIndexes(state);
  if (!indexes.length) return 0;
  if (indexes.includes(index)) return index;
  return indexes[0];
}

/** @param {GalleryState} state @param {number} index */
function inlineItem(state, index) {
  const normalized = normalizeIndex(state, index);
  if (state.gallery.dataset.presentation === 'grid') {
    return state.gallery.querySelector(`.media-gallery__grid > li[data-aw-media-index="${normalized}"]`);
  }
  return state.slideByIndex.get(normalized) || null;
}

/** @param {GalleryState} state @param {number} index */
function fullscreenItem(state, index) {
  return state.zoomDialog?.querySelector?.(
    `.dialog-zoomed-gallery > li[data-aw-media-index="${Number(index) || 0}"]`
  ) || null;
}

/** @param {GalleryState} state @param {number} index @param {'inline'|'fullscreen'|'closing'} [context] */
function contextItem(state, index, context = state.context) {
  return context === 'fullscreen' ? fullscreenItem(state, index) : inlineItem(state, index);
}

/** @param {GalleryState} state */
function currentInlineIndex(state) {
  const current = Number(state.slideshow.current);
  const componentSlides = state.slideshow.slides || state.slideshow.refs?.slides;
  const currentSlide = componentSlides && Number.isInteger(current)
    ? Array.from(componentSlides)[current]
    : null;
  if (currentSlide instanceof Element) return mediaIndex(currentSlide);

  // During Horizon's infinite edge transition the old and target slides can
  // briefly both be aria-visible. Prefer the component's authoritative current
  // index above; this fallback is only for the pre-initialisation/no-JS path.
  const visible = getInlineSlides(state).find((slide) => slide.getAttribute('aria-hidden') !== 'true');
  return visible ? mediaIndex(visible) : state.activeIndex;
}

/** @param {Element | null | undefined} item */
function isVideoItem(item) {
  return item instanceof HTMLElement && Boolean(item.querySelector(VIDEO_ROOT_SELECTOR));
}

/** @param {Element | null | undefined} item */
function videoRoot(item) {
  return item?.querySelector?.(VIDEO_ROOT_SELECTOR) || null;
}

/** @param {Element | null | undefined} item */
function deferredMedia(item) {
  return item?.querySelector?.('deferred-media') || null;
}

/** @param {Element | null | undefined} item */
function nativeVideo(item) {
  return item?.querySelector?.('video') || null;
}

/** @param {Element | null | undefined} item */
function externalVideo(item) {
  return item?.querySelector?.('iframe[data-video-type]') || null;
}

/** @param {GalleryState} state @param {HTMLElement} item @param {boolean} shouldPlay */
function beginPlaybackCommand(state, item, shouldPlay) {
  const token = ++state.playbackSerial;
  state.playbackTokens.set(item, token);
  state.playbackIntent.set(item, Boolean(shouldPlay));
  return token;
}

/** @param {GalleryState} state @param {HTMLElement} item @param {number} token @param {boolean} shouldPlay */
function playbackCommandIsCurrent(state, item, token, shouldPlay) {
  return state.playbackTokens.get(item) === token && state.playbackIntent.get(item) === Boolean(shouldPlay);
}

/** @param {GalleryState} state @param {HTMLElement} item */
function playbackIsIntended(state, item) {
  return state.playbackIntent.get(item) === true;
}

/** @param {GalleryState} state @param {HTMLElement} item */
function playbackIsPending(state, item) {
  const token = state.pendingPlayback.get(item);
  return Number.isInteger(token) && playbackCommandIsCurrent(state, item, token, true);
}

/** @param {unknown} error */
function isAutoplayPolicyError(error) {
  const name = error && typeof error === 'object' && 'name' in error ? String(error.name) : '';
  return name === 'NotAllowedError';
}

/** @param {GalleryState} state @param {number} index */
function isManuallyPaused(state, index) {
  return state.manualPause?.index === index;
}

/** @param {GalleryState} state */
function playbackSurfaceIsVisible(state) {
  if (!state.pageVisible || state.context === 'closing') return false;
  if (state.context === 'fullscreen') return true;
  return state.galleryVisible;
}

/** @param {GalleryState} state @param {HTMLElement} item */
function itemMayPlay(state, item) {
  return itemIsActive(state, item) && playbackSurfaceIsVisible(state) && !isManuallyPaused(state, mediaIndex(item));
}

/** @param {GalleryState} state @param {HTMLElement} item */
function desiredAudible(state, item) {
  const memory = state.videoMemory.get(mediaIndex(item));
  if (typeof memory?.audible === 'boolean') return memory.audible;
  const root = videoRoot(item);
  if (root?.dataset.awVideoDesiredAudible === 'true') return true;
  if (root?.dataset.awVideoDesiredAudible === 'false') return false;
  return pageSoundPreference !== false;
}

/** @param {GalleryState} state @param {HTMLElement} item @param {boolean} audible */
function setDesiredAudible(state, item, audible) {
  const index = mediaIndex(item);
  const memory = state.videoMemory.get(index) || {};
  memory.audible = Boolean(audible);
  state.videoMemory.set(index, memory);
  const root = videoRoot(item);
  if (root instanceof HTMLElement) root.dataset.awVideoDesiredAudible = audible ? 'true' : 'false';
}

/** @param {HTMLElement} element */
function elementIsMeaningfullyVisible(element) {
  if (!(element instanceof HTMLElement) || !element.isConnected) return false;
  const rect = element.getBoundingClientRect();
  const viewportWidth = Math.max(document.documentElement.clientWidth || 0, window.innerWidth || 0);
  const viewportHeight = Math.max(document.documentElement.clientHeight || 0, window.innerHeight || 0);
  if (rect.width <= 0 || rect.height <= 0 || viewportWidth <= 0 || viewportHeight <= 0) return false;

  const left = Math.max(rect.left, 0);
  const right = Math.min(rect.right, viewportWidth);
  const top = Math.max(rect.top, VIEWPORT_INSET_PX);
  const bottom = Math.min(rect.bottom, Math.max(VIEWPORT_INSET_PX, viewportHeight - VIEWPORT_INSET_PX));
  const minimumWidth = Math.min(48, rect.width * 0.2);
  const minimumHeight = Math.min(48, rect.height * 0.12);
  return right - left >= minimumWidth && bottom - top >= minimumHeight;
}

/** @param {GalleryState} state */
function suspendActivePlayback(state) {
  state.activationToken += 1;
  const item = contextItem(state, state.activeIndex, state.context === 'fullscreen' ? 'fullscreen' : 'inline');
  pauseAllExcept(state, item instanceof HTMLElement ? item : null);
  if (item instanceof HTMLElement && isVideoItem(item)) {
    pauseItem(state, item, { hideControls: false, forceMuted: true, remember: true });
  } else {
    refreshVideoUi(state);
  }
}

/** @param {GalleryState} state @param {boolean} visible */
function setGalleryVisibility(state, visible) {
  const next = Boolean(visible);
  if (state.galleryVisible === next) return;
  state.galleryVisible = next;
  state.gallery.dataset.awGalleryVisible = next ? 'true' : 'false';
  if (state.context === 'fullscreen' || state.context === 'closing') return;

  if (!next) {
    suspendActivePlayback(state);
    return;
  }

  void activate(state, state.activeIndex, {
    context: 'inline',
    userInitiated: false,
    announce: false,
  });
}

/** @param {GalleryState} state */
function installViewportObserver(state) {
  state.galleryVisible = elementIsMeaningfullyVisible(state.gallery);
  state.gallery.dataset.awGalleryVisible = state.galleryVisible ? 'true' : 'false';

  if ('IntersectionObserver' in window) {
    state.viewportObserver = new IntersectionObserver((entries) => {
      const entry = entries.find((candidate) => candidate.target === state.gallery);
      if (!entry) return;
      const visible = entry.isIntersecting &&
        entry.intersectionRect.width >= Math.min(48, entry.boundingClientRect.width * 0.2) &&
        entry.intersectionRect.height >= Math.min(48, entry.boundingClientRect.height * 0.12);
      setGalleryVisibility(state, visible);
    }, {
      root: null,
      rootMargin: `-${VIEWPORT_INSET_PX}px 0px -${VIEWPORT_INSET_PX}px 0px`,
      threshold: [0, 0.01, 0.04, 0.08, 0.2],
    });
    state.viewportObserver.observe(state.gallery);
    return;
  }

  const check = () => {
    state.visibilityFrame = null;
    if (state.gallery.isConnected) setGalleryVisibility(state, elementIsMeaningfullyVisible(state.gallery));
  };
  const schedule = () => {
    if (state.visibilityFrame != null) return;
    state.visibilityFrame = requestAnimationFrame(check);
  };
  window.addEventListener('scroll', schedule, { passive: true, signal: state.controller.signal });
}

/** @param {GalleryState} state */
function dominantInlineIndex(state) {
  const scrollerRect = state.scroller.getBoundingClientRect();
  if (scrollerRect.width <= 0) return null;
  const viewportCenter = scrollerRect.left + scrollerRect.width / 2;
  let best = null;

  for (const slide of getInlineSlides(state)) {
    const rect = slide.getBoundingClientRect();
    if (rect.width <= 0) continue;
    const overlap = Math.max(0, Math.min(rect.right, scrollerRect.right) - Math.max(rect.left, scrollerRect.left));
    const ratio = overlap / Math.min(rect.width, scrollerRect.width);
    const centerDistance = Math.abs(rect.left + rect.width / 2 - viewportCenter) / scrollerRect.width;
    const score = ratio - centerDistance * 0.16;
    if (!best || score > best.score) best = { index: mediaIndex(slide), ratio, score };
  }

  return best && best.ratio >= 0.56 ? best.index : null;
}

/** @param {GalleryState} state @param {number} index */
function syncSlideshowCurrent(state, index) {
  const componentSlides = state.slideshow.slides || state.slideshow.refs?.slides;
  if (!componentSlides) return;
  const position = Array.from(componentSlides).findIndex((slide) => mediaIndex(slide) === index);
  if (position < 0 || Number(state.slideshow.current) === position) return;

  // Horizon normally performs this assignment before dispatching
  // slideshow:select. The geometry fallback only reaches this branch if the
  // browser completed native scroll-snap without that event; mirroring the
  // public current setter keeps later section/mutation refreshes authoritative.
  try {
    state.slideshow.current = position;
  } catch {
    // A still-upgrading custom element remains safe: activeIndex continues to
    // drive playback and the next Horizon event reconciles component state.
  }
}

/** @param {GalleryState} state @param {boolean} [immediate] */
function scheduleScrollSelection(state, immediate = false) {
  if (state.context !== 'inline') return;
  if (state.scrollSettleTimer != null) window.clearTimeout(state.scrollSettleTimer);
  state.scrollSettleTimer = window.setTimeout(() => {
    state.scrollSettleTimer = null;
    if (!state.gallery.isConnected || state.context !== 'inline') return;
    if (state.edgePointer || state.slideshow.hasAttribute('dragging')) {
      scheduleScrollSelection(state, false);
      return;
    }
    const index = dominantInlineIndex(state);
    if (index == null || normalizeIndex(state, index) === state.activeIndex) return;
    syncSlideshowCurrent(state, index);
    void activate(state, index, { context: 'inline', userInitiated: false, announce: false });
  }, immediate ? 0 : SCROLL_SETTLE_MS);
}

/** @param {GalleryState} state */
function validateGalleryContract(state) {
  const issues = [];
  if (state.gallery.dataset.awGalleryContract !== GALLERY_CONTRACT_VERSION) {
    issues.push(`root version ${state.gallery.dataset.awGalleryContract || 'missing'}`);
  }
  for (const item of videoItems(state)) {
    const root = videoRoot(item);
    if (!root?.querySelector('deferred-media[data-aw-gallery-managed]')) issues.push(`managed player ${mediaIndex(item)}`);
    if (!root?.querySelector(PLAY_SELECTOR)) issues.push(`play control ${mediaIndex(item)}`);
    if (!root?.querySelector(SOUND_SELECTOR)) issues.push(`sound control ${mediaIndex(item)}`);
  }

  state.gallery.dataset.awGalleryContractStatus = issues.length ? 'degraded' : 'verified';
  if (issues.length && !state.contractWarningIssued) {
    state.contractWarningIssued = true;
    console.error(`[ANTLITZWERK] Protected product-gallery contract ${GALLERY_CONTRACT_VERSION} is incomplete: ${issues.join(', ')}`);
  }
}

/** @param {HTMLVideoElement} video */
async function playNativeWithTimeout(video) {
  const result = video.play();
  if (!result || typeof result.then !== 'function') return;
  let timeoutId = null;
  try {
    await Promise.race([
      result,
      new Promise((_, reject) => {
        timeoutId = window.setTimeout(() => {
          const error = new Error('Product video play request timed out');
          error.name = 'TimeoutError';
          reject(error);
        }, PLAY_PROMISE_TIMEOUT_MS);
      }),
    ]);
  } finally {
    if (timeoutId != null) window.clearTimeout(timeoutId);
  }
}

/** @param {GalleryState} state @param {HTMLElement} item */
async function discoverPlayer(state, item) {
  let loaded = ensureLoaded(state, item);
  if (loaded.video || loaded.iframe) return loaded;

  return new Promise((resolve) => {
    let settled = false;
    const finish = () => {
      if (settled) return;
      const result = ensureLoaded(state, item);
      if (!result.video && !result.iframe && state.gallery.isConnected) return;
      settled = true;
      observer.disconnect();
      window.clearTimeout(timer);
      resolve(result);
    };
    const observer = new MutationObserver(finish);
    observer.observe(item, { childList: true, subtree: true });
    const timer = window.setTimeout(() => {
      if (settled) return;
      settled = true;
      observer.disconnect();
      resolve(ensureLoaded(state, item));
    }, PLAYER_DISCOVERY_TIMEOUT_MS);
    queueMicrotask(finish);
  });
}

/** @param {HTMLIFrameElement} iframe @param {string} command @param {unknown} [value] */
function sendExternalCommand(iframe, command, value) {
  if (!(iframe instanceof HTMLIFrameElement)) return;

  if (iframe.dataset.videoType === 'youtube') {
    iframe.contentWindow?.postMessage(
      JSON.stringify({ event: 'command', func: command, args: value === undefined ? [] : [value] }),
      '*'
    );
    return;
  }

  const payload = { method: command };
  if (value !== undefined) payload.value = value;
  iframe.contentWindow?.postMessage(payload, '*');
}

/** @param {HTMLVideoElement} video */
function configureNativeVideo(video) {
  // The protected gallery owns playback. Removing markup autoplay eliminates
  // the Horizon/Safari double-play race while explicit muted play() remains
  // fully standards-compliant and immediate.
  video.autoplay = false;
  video.loop = true;
  video.controls = false;
  video.playsInline = true;
  video.disablePictureInPicture = true;
  video.removeAttribute('autoplay');
  video.setAttribute('loop', '');
  video.setAttribute('playsinline', '');
  video.setAttribute('webkit-playsinline', '');
  video.setAttribute('disablepictureinpicture', '');
  video.setAttribute('disableremoteplayback', '');
  if (!video.preload || video.preload === 'none') video.preload = 'metadata';
}

/** @param {HTMLVideoElement} video @param {boolean} muted */
function setNativeMuted(video, muted) {
  configureNativeVideo(video);
  video.muted = Boolean(muted);
  video.defaultMuted = Boolean(muted);
  if (muted) {
    video.setAttribute('muted', '');
  } else {
    video.removeAttribute('muted');
    if (video.volume === 0) video.volume = 1;
  }
}

/** @param {HTMLIFrameElement} iframe @param {boolean} muted */
function setExternalMuted(iframe, muted) {
  if (iframe.dataset.videoType === 'youtube') {
    sendExternalCommand(iframe, muted ? 'mute' : 'unMute');
    if (!muted) sendExternalCommand(iframe, 'setVolume', 100);
  } else {
    sendExternalCommand(iframe, 'setVolume', muted ? 0 : 1);
  }
}

/** @param {GalleryState} state @param {HTMLElement} item @param {HTMLIFrameElement} iframe */
function applyExternalPlaybackIntent(state, item, iframe) {
  const root = videoRoot(item);
  const active = itemIsActive(state, item);
  const shouldPlay = itemMayPlay(state, item) && playbackIsIntended(state, item);
  const wantAudible = root?.dataset.awVideoPlayAudible === 'true';
  const deferred = deferredMedia(item);

  if (shouldPlay) {
    setExternalMuted(iframe, !wantAudible);
    if (iframe.dataset.videoType === 'youtube') {
      sendExternalCommand(iframe, 'playVideo');
    } else {
      sendExternalCommand(iframe, 'setLoop', true);
      sendExternalCommand(iframe, 'play');
    }
    if (deferred && 'isPlaying' in deferred) deferred.isPlaying = true;
    setVideoUi(state, item, {
      active: true,
      loaded: true,
      playing: true,
      audible: wantAudible,
      pending: false,
    });
    return;
  }

  sendExternalCommand(iframe, iframe.dataset.videoType === 'youtube' ? 'pauseVideo' : 'pause');
  setExternalMuted(iframe, true);
  if (deferred && 'isPlaying' in deferred) deferred.isPlaying = false;
  setVideoUi(state, item, {
    active,
    loaded: true,
    playing: false,
    audible: false,
    pending: false,
  });
}

/** @param {GalleryState} state @param {HTMLElement} item @param {HTMLIFrameElement} iframe */
function bindExternalVideo(state, item, iframe) {
  iframe.tabIndex = -1;
  iframe.setAttribute('aria-hidden', 'true');
  if (state.boundExternalVideos.has(iframe)) return;
  state.boundExternalVideos.add(iframe);

  iframe.addEventListener('load', () => {
    requestAnimationFrame(() => {
      if (state.gallery.isConnected) applyExternalPlaybackIntent(state, item, iframe);
    });
  }, { signal: state.controller.signal });
}

/** @param {GalleryState} state @param {HTMLElement} item */
function itemIsActive(state, item) {
  const index = mediaIndex(item);
  const inFullscreen = Boolean(item.closest('zoom-dialog'));
  return index === state.activeIndex && (
    (state.context === 'fullscreen' && inFullscreen) ||
    (state.context === 'inline' && !inFullscreen)
  );
}

/** @param {GalleryState} state @param {HTMLElement} item @param {HTMLVideoElement} video */
function bindNativeVideo(state, item, video) {
  configureNativeVideo(video);
  if (state.boundVideos.has(video)) return;
  state.boundVideos.add(video);

  const sync = (event) => {
    const active = itemIsActive(state, item);
    const intended = playbackIsIntended(state, item);
    const pending = playbackIsPending(state, item);
    const mayPlay = itemMayPlay(state, item);

    // Any hidden/off-screen representation is stopped immediately. A transient
    // pause while play() is pending is intentionally ignored; the command token
    // decides the outcome, preventing the former pause→play deadlock.
    if ((!active || !intended || !mayPlay) && !video.paused) {
      if (active) rememberPlayback(state, item);
      video.pause();
      if (!active || !mayPlay) setNativeMuted(video, true);
    } else if (event?.type === 'pause' && intended && !pending && active && mayPlay && video.paused) {
      beginPlaybackCommand(state, item, false);
    }

    if (event?.type === 'timeupdate' && active && Number.isFinite(video.currentTime)) {
      const now = performance.now();
      const last = state.timeMemoryAt.get(video) || 0;
      if (now - last >= TIME_MEMORY_INTERVAL_MS) {
        state.timeMemoryAt.set(video, now);
        rememberPlayback(state, item);
      }
    }

    const playing = !video.paused && !video.ended;
    const audible = !video.muted && video.volume > 0;
    setVideoUi(state, item, { active, loaded: true, playing, audible, pending });
  };

  for (const name of ['play', 'playing', 'pause', 'ended', 'volumechange', 'loadedmetadata', 'emptied', 'timeupdate']) {
    video.addEventListener(name, sync, { signal: state.controller.signal });
  }
  sync();
}

/**
 * Instantiates the exact active player synchronously whenever Horizon is ready.
 * A short discovery wait in discoverPlayer() covers custom-element upgrade or
 * section-morph timing without reloading sources or lowering media quality.
 * @param {GalleryState} state
 * @param {HTMLElement} item
 */
function ensureLoaded(state, item) {
  const deferred = deferredMedia(item);
  if (deferred) deferred.removeAttribute('autoplay');
  if (deferred && !deferred.hasAttribute('data-media-loaded')) {
    if (typeof deferred.loadContent === 'function') {
      deferred.loadContent(false);
    } else {
      const content = deferred.querySelector('template')?.content.firstElementChild?.cloneNode(true);
      if (content) {
        deferred.setAttribute('data-media-loaded', 'true');
        deferred.appendChild(content);
        deferred.querySelector('[ref="deferredMediaPlayButton"]')?.classList.add('deferred-media__playing');
      }
    }
  }

  const video = nativeVideo(item);
  const iframe = externalVideo(item);
  if (video instanceof HTMLVideoElement) bindNativeVideo(state, item, video);
  if (iframe instanceof HTMLIFrameElement) bindExternalVideo(state, item, iframe);

  const root = videoRoot(item);
  if (root instanceof HTMLElement && root.dataset.awVideoDesiredAudible == null) {
    root.dataset.awVideoDesiredAudible = desiredAudible(state, item) ? 'true' : 'false';
  }

  return { deferred, video, iframe };
}

/** @param {GalleryState} state @param {HTMLElement} item */
function rememberPlayback(state, item) {
  const index = mediaIndex(item);
  const memory = state.videoMemory.get(index) || {};
  const video = nativeVideo(item);
  if (video instanceof HTMLVideoElement && Number.isFinite(video.currentTime)) {
    memory.time = Math.max(0, video.currentTime);
  }
  memory.audible = desiredAudible(state, item);
  state.videoMemory.set(index, memory);
}

/** @param {GalleryState} state @param {HTMLElement} item @param {HTMLVideoElement} video @param {number} commandToken */
function restorePlaybackTime(state, item, video, commandToken) {
  const memory = state.videoMemory.get(mediaIndex(item));
  const rememberedTime = Number(memory?.time);
  if (!Number.isFinite(rememberedTime) || rememberedTime <= 0) return;

  const apply = () => {
    if (!playbackCommandIsCurrent(state, item, commandToken, true)) return;

    try {
      const duration = Number(video.duration);
      let targetTime = Number.isFinite(duration) && duration > 0
        ? Math.min(rememberedTime, Math.max(0, duration - 0.05))
        : rememberedTime;

      const currentTime = Number(video.currentTime) || 0;
      // Resuming the same element should never seek to an almost-identical
      // timestamp: that flushes decoders in Safari/Chromium and creates a tiny
      // hitch. Only an actual inline↔fullscreen hand-off near the loop boundary
      // begins cleanly at frame zero instead of flashing the final frame.
      if (Math.abs(currentTime - targetTime) <= RESUME_SEEK_EPSILON_SECONDS) return;
      if (Number.isFinite(duration) && duration > 0 && duration - targetTime <= LOOP_RESTORE_GUARD_SECONDS) {
        targetTime = 0;
      }
      if (Math.abs(currentTime - targetTime) <= RESUME_SEEK_EPSILON_SECONDS) return;
      video.currentTime = targetTime;
    } catch {
      // Some browsers reject currentTime until metadata is ready; the one-shot
      // loadedmetadata listener below covers that path.
    }
  };

  if (video.readyState >= 1) apply();
  else video.addEventListener('loadedmetadata', apply, { once: true, signal: state.controller.signal });
}

/**
 * @param {GalleryState} state
 * @param {HTMLElement} item
 * @param {{active:boolean,loaded:boolean,playing:boolean,audible:boolean,pending?:boolean}} ui
 */
function setVideoUi(state, item, ui) {
  const pending = Boolean(ui.pending);
  const playingOrPending = Boolean(ui.playing || pending);
  const playButton = item.querySelector(PLAY_SELECTOR);
  if (playButton instanceof HTMLButtonElement) {
    playButton.classList.remove('hidden');
    playButton.hidden = !ui.active;
    playButton.classList.toggle('is-playing', playingOrPending);
    playButton.classList.toggle('is-paused', !playingOrPending);
    playButton.setAttribute('aria-pressed', playingOrPending ? 'true' : 'false');
    playButton.setAttribute('aria-busy', pending ? 'true' : 'false');

    const icon = playButton.querySelector('.icon-play');
    if (icon instanceof HTMLElement) icon.classList.toggle('hidden', playingOrPending);
    const label = playingOrPending ? playButton.dataset.labelPause : playButton.dataset.labelPlay;
    if (label) playButton.setAttribute('aria-label', label);
  }

  const soundButton = item.querySelector(SOUND_SELECTOR);
  if (soundButton instanceof HTMLButtonElement) {
    const label = ui.audible
      ? (soundButton.dataset.awLabelDisable || localeText().soundOff)
      : (soundButton.dataset.awLabelEnable || localeText().soundOn);
    soundButton.hidden = !(ui.active && ui.loaded);
    soundButton.classList.toggle('is-audible', ui.audible);
    soundButton.setAttribute('aria-pressed', ui.audible ? 'true' : 'false');
    soundButton.setAttribute('aria-label', label);
    soundButton.setAttribute('title', label);

    const mutedIcon = soundButton.querySelector('[data-aw-sound-icon-muted]');
    const audibleIcon = soundButton.querySelector('[data-aw-sound-icon-audible]');
    if (mutedIcon instanceof HTMLElement) mutedIcon.hidden = ui.audible;
    if (audibleIcon instanceof HTMLElement) audibleIcon.hidden = !ui.audible;
    const srLabel = soundButton.querySelector('[data-aw-sound-label]');
    if (srLabel) srLabel.textContent = label;
  }

  const expandButton = item.querySelector(EXPAND_SELECTOR);
  if (expandButton instanceof HTMLButtonElement) {
    expandButton.hidden = !(ui.active && state.context === 'inline' && Boolean(state.zoomDialog));
  }

  const root = videoRoot(item);
  if (root instanceof HTMLElement) {
    root.dataset.awVideoPlaying = ui.playing ? 'true' : 'false';
    root.dataset.awVideoPending = pending ? 'true' : 'false';
    root.dataset.awVideoAudible = ui.audible ? 'true' : 'false';
  }
}

/**
 * @param {GalleryState} state
 * @param {HTMLElement} item
 * @param {{hideControls?:boolean,forceMuted?:boolean,remember?:boolean}} [options]
 */
function pauseItem(state, item, options = {}) {
  if (!isVideoItem(item)) return;
  const { hideControls = true, forceMuted = true, remember = true } = options;
  const video = nativeVideo(item);
  const iframe = externalVideo(item);
  const deferred = deferredMedia(item);
  const wasPlaying = video instanceof HTMLVideoElement && !video.paused && !video.ended;

  // Capture the active timestamp and the user's chosen sound state before any
  // forced mute. Hidden inline/fullscreen duplicates never overwrite it.
  if (remember && (itemIsActive(state, item) || wasPlaying)) rememberPlayback(state, item);
  beginPlaybackCommand(state, item, false);
  state.pendingPlayback.delete(item);

  if (video instanceof HTMLVideoElement) {
    video.pause();
    if (forceMuted) setNativeMuted(video, true);
    if (hideControls && forceMuted) video.preload = 'metadata';
  }

  if (iframe instanceof HTMLIFrameElement) {
    sendExternalCommand(iframe, iframe.dataset.videoType === 'youtube' ? 'pauseVideo' : 'pause');
    if (forceMuted) setExternalMuted(iframe, true);
  }
  if (deferred && 'isPlaying' in deferred) deferred.isPlaying = false;

  const audible = !forceMuted && (
    video instanceof HTMLVideoElement
      ? !video.muted && video.volume > 0
      : videoRoot(item)?.dataset.awVideoAudible === 'true'
  );
  const root = videoRoot(item);
  if (root instanceof HTMLElement) root.dataset.awVideoPlayAudible = audible ? 'true' : 'false';
  setVideoUi(state, item, {
    active: !hideControls && itemIsActive(state, item),
    loaded: Boolean(video || iframe),
    playing: false,
    audible,
    pending: false,
  });
}

/** @param {GalleryState} state */
function videoItems(state) {
  return [...state.gallery.querySelectorAll(VIDEO_ITEM_SELECTOR)]
    .filter((item) => item instanceof HTMLElement && isVideoItem(item));
}

/** @param {GalleryState} state @param {HTMLElement | null} keep */
function pauseAllExcept(state, keep = null) {
  for (const item of videoItems(state)) {
    if (item === keep) continue;
    pauseItem(state, item, { hideControls: true, forceMuted: true, remember: true });
  }
}

/**
 * @param {GalleryState} state
 * @param {HTMLElement} item
 * @param {{userInitiated?:boolean}} [options]
 */
async function playItem(state, item, options = {}) {
  if (!isVideoItem(item) || !itemMayPlay(state, item)) return false;
  pauseAllExcept(state, item);

  const commandToken = beginPlaybackCommand(state, item, true);
  state.pendingPlayback.set(item, commandToken);
  setVideoUi(state, item, {
    active: true,
    loaded: Boolean(nativeVideo(item) || externalVideo(item)),
    playing: true,
    audible: false,
    pending: true,
  });

  const { deferred, video, iframe } = await discoverPlayer(state, item);
  if (!playbackCommandIsCurrent(state, item, commandToken, true) || !itemMayPlay(state, item)) {
    state.pendingPlayback.delete(item);
    if (video instanceof HTMLVideoElement && !video.paused) video.pause();
    return false;
  }

  // Every automatic entry/resume first requests the visitor's desired sound
  // state. The page default is audible. If browser autoplay policy rejects that
  // request, the guarded branch below immediately retries muted; the sound
  // control can then enable audio directly on the visitor's next tap/click.
  const wantAudible = desiredAudible(state, item);
  const root = videoRoot(item);
  if (root instanceof HTMLElement) root.dataset.awVideoPlayAudible = wantAudible ? 'true' : 'false';

  if (video instanceof HTMLVideoElement) {
    video.preload = 'auto';
    restorePlaybackTime(state, item, video, commandToken);
    setNativeMuted(video, !wantAudible);
    setVideoUi(state, item, {
      active: true,
      loaded: true,
      playing: true,
      audible: !video.muted && video.volume > 0,
      pending: true,
    });

    let playError = null;
    try {
      await playNativeWithTimeout(video);
    } catch (error) {
      playError = error;
    }

    if (!playbackCommandIsCurrent(state, item, commandToken, true)) {
      state.pendingPlayback.delete(item);
      if (!playbackIsIntended(state, item) && !video.paused) video.pause();
      return false;
    }

    // Only an audible autoplay-policy rejection retries muted. Abort/timeout or
    // source interruption ends this command; an older promise can never revive it.
    if (playError) {
      if (wantAudible && isAutoplayPolicyError(playError)) {
        setNativeMuted(video, true);
        if (root instanceof HTMLElement) root.dataset.awVideoPlayAudible = 'false';
        try {
          await playNativeWithTimeout(video);
        } catch {
          if (playbackCommandIsCurrent(state, item, commandToken, true)) {
            beginPlaybackCommand(state, item, false);
            state.pendingPlayback.delete(item);
            video.pause();
            setVideoUi(state, item, {
              active: true,
              loaded: true,
              playing: false,
              audible: false,
              pending: false,
            });
          }
          return false;
        }
      } else {
        beginPlaybackCommand(state, item, false);
        state.pendingPlayback.delete(item);
        video.pause();
        setVideoUi(state, item, {
          active: true,
          loaded: true,
          playing: false,
          audible: false,
          pending: false,
        });
        return false;
      }
    }

    state.pendingPlayback.delete(item);
    if (!playbackCommandIsCurrent(state, item, commandToken, true)) {
      if (!playbackIsIntended(state, item) && !video.paused) video.pause();
      return false;
    }
    if (!itemMayPlay(state, item)) {
      pauseItem(state, item, { hideControls: false, forceMuted: true, remember: true });
      return false;
    }

    const playing = !video.paused && !video.ended;
    const audible = playing && !video.muted && video.volume > 0;
    if (!playing) beginPlaybackCommand(state, item, false);
    if (deferred && 'isPlaying' in deferred) deferred.isPlaying = playing;
    setVideoUi(state, item, {
      active: true,
      loaded: true,
      playing,
      audible,
      pending: false,
    });
    return playing;
  }

  if (iframe instanceof HTMLIFrameElement) {
    state.pendingPlayback.delete(item);
    applyExternalPlaybackIntent(state, item, iframe);
    return playbackCommandIsCurrent(state, item, commandToken, true) && itemMayPlay(state, item);
  }

  beginPlaybackCommand(state, item, false);
  state.pendingPlayback.delete(item);
  setVideoUi(state, item, {
    active: true,
    loaded: false,
    playing: false,
    audible: false,
    pending: false,
  });
  return false;
}

/** @param {GalleryState} state @param {HTMLElement} item @param {Event} event */
function toggleSound(state, item, event) {
  stopEvent(event);
  if (!itemIsActive(state, item)) return;

  // Sound is intentionally orthogonal to playback: it never starts, pauses or
  // reloads media. The exact state survives a manual Pause → Play cycle.
  const video = nativeVideo(item);
  const iframe = externalVideo(item);
  if (video instanceof HTMLVideoElement) {
    const nextAudible = video.muted || video.volume === 0;
    pageSoundPreference = nextAudible;
    setDesiredAudible(state, item, nextAudible);
    setNativeMuted(video, !nextAudible);
    const root = videoRoot(item);
    if (root instanceof HTMLElement) root.dataset.awVideoPlayAudible = nextAudible ? 'true' : 'false';
    rememberPlayback(state, item);
    setVideoUi(state, item, {
      active: true,
      loaded: true,
      playing: !video.paused && !video.ended,
      audible: !video.muted && video.volume > 0,
      pending: playbackIsPending(state, item),
    });
    return;
  }

  if (iframe instanceof HTMLIFrameElement) {
    const root = videoRoot(item);
    const currentlyAudible = root?.dataset.awVideoAudible === 'true';
    const nextAudible = !currentlyAudible;
    pageSoundPreference = nextAudible;
    setDesiredAudible(state, item, nextAudible);
    setExternalMuted(iframe, !nextAudible);
    if (root instanceof HTMLElement) {
      root.dataset.awVideoPlayAudible = nextAudible ? 'true' : 'false';
      root.dataset.awVideoAudible = nextAudible ? 'true' : 'false';
    }
    setVideoUi(state, item, {
      active: true,
      loaded: true,
      playing: root?.dataset.awVideoPlaying === 'true',
      audible: nextAudible,
      pending: false,
    });
  }
}

/** @param {GalleryState} state @param {HTMLElement} item @param {Event} event */
async function togglePlayback(state, item, event) {
  stopEvent(event);
  if (!itemIsActive(state, item)) return;

  const video = nativeVideo(item);
  const root = videoRoot(item);
  const actuallyPlaying = video instanceof HTMLVideoElement
    ? !video.paused && !video.ended
    : root?.dataset.awVideoPlaying === 'true';
  const playPendingOrIntended = playbackIsPending(state, item) || playbackIsIntended(state, item);

  if (actuallyPlaying || playPendingOrIntended) {
    state.manualPause = { index: mediaIndex(item) };
    pauseItem(state, item, { hideControls: false, forceMuted: false, remember: true });
    return;
  }

  state.manualPause = null;
  await playItem(state, item, { userInitiated: true });
}

/** @param {HTMLElement} gallery */
function createFooter(gallery) {
  const footer = document.createElement('div');
  footer.className = 'aw-product-gallery__footer';
  footer.dataset.awGalleryFooter = '';
  footer.innerHTML = `
    <span class="aw-product-gallery__track" aria-hidden="true"><i data-aw-progress></i></span>
    <span class="visually-hidden" data-aw-live aria-live="polite" aria-atomic="true"></span>
  `;
  gallery.appendChild(footer);
  return footer;
}

/** @param {number} position @param {number} total */
function mappedProgressPosition(position, total) {
  const ratio = total > 1 ? Math.max(0, Math.min(total - 1, position)) / (total - 1) : 0.5;
  return PROGRESS_EDGE_INSET + ratio * (100 - PROGRESS_EDGE_INSET * 2);
}

/** @param {GalleryState} state */
function ensureProgressElements(state) {
  if (
    state.progress instanceof HTMLElement &&
    state.progress.isConnected &&
    state.progressTrack instanceof HTMLElement &&
    state.progressTrack.isConnected
  ) return state.progress;

  const footer = state.gallery.querySelector('[data-aw-gallery-footer]') || createFooter(state.gallery);
  const track = footer.querySelector('.aw-product-gallery__track');
  const progress = footer.querySelector('[data-aw-progress]');
  state.progressTrack = track instanceof HTMLElement ? track : null;
  state.progress = progress instanceof HTMLElement ? progress : null;
  return state.progress;
}

/** @param {GalleryState} state */
function updateProgressMetrics(state) {
  ensureProgressElements(state);
  if (!(state.progressTrack instanceof HTMLElement)) {
    state.progressTrackWidth = 0;
    return;
  }
  state.progressTrackWidth = Math.max(1, state.progressTrack.getBoundingClientRect().width || 1);
}

/** @param {GalleryState} state @param {number} position */
function progressTransform(state, position) {
  const width = Math.max(1, state.progressTrackWidth || 1);
  const x = snapToDevicePixel(width * clampNumber(position, 0, 100) / 100);
  return `translate3d(${x}px, -50%, 0) translateX(-50%)`;
}

/** @param {GalleryState} state @param {boolean} active */
function setInlineProgressFollowing(state, active) {
  ensureProgressElements(state);
  if (!(state.progressTrack instanceof HTMLElement)) return;

  if (active) state.progressTrack.setAttribute('data-aw-progress-following', '');
  else state.progressTrack.removeAttribute('data-aw-progress-following');
}

/** @param {GalleryState} state */
function inlineProgressIsFollowing(state) {
  return state.progressTrack instanceof HTMLElement &&
    state.progressTrack.hasAttribute('data-aw-progress-following');
}

/**
 * Maps the physical carousel position to the semantic media positions. Geometry
 * is measured only on init/resize/morph, never on every scroll frame. Sorting
 * by the live DOM position also keeps Horizon's first↔last loop reordering
 * visually continuous without touching its scrolling or media lifecycle.
 * @param {GalleryState} state
 */
function interpolatedInlineProgressPosition(state) {
  const indexes = getAvailableIndexes(state);
  const total = indexes.length;
  if (total <= 1) return mappedProgressPosition(0.5, Math.max(total, 1));

  if (state.inlineProgressGeometry.length !== total || state.inlineViewportWidth <= 1) {
    updateInlineGeometry(state);
  }

  const geometry = state.inlineProgressGeometry;
  if (!geometry.length) {
    const fallbackPosition = Math.max(0, indexes.indexOf(state.activeIndex));
    return mappedProgressPosition(fallbackPosition, total);
  }

  const viewportCenter = state.scroller.scrollLeft + state.inlineViewportWidth / 2;
  const first = geometry[0];
  const last = geometry[geometry.length - 1];

  if (viewportCenter <= first.center) return mappedProgressPosition(first.position, total);
  if (viewportCenter >= last.center) return mappedProgressPosition(last.position, total);

  for (let i = 0; i < geometry.length - 1; i += 1) {
    const left = geometry[i];
    const right = geometry[i + 1];
    if (viewportCenter < left.center || viewportCenter > right.center) continue;

    const span = Math.max(1, right.center - left.center);
    const ratio = clampNumber((viewportCenter - left.center) / span, 0, 1);
    const leftPosition = mappedProgressPosition(left.position, total);
    const rightPosition = mappedProgressPosition(right.position, total);
    return leftPosition + (rightPosition - leftPosition) * ratio;
  }

  const fallbackPosition = Math.max(0, indexes.indexOf(state.activeIndex));
  return mappedProgressPosition(fallbackPosition, total);
}

/** @param {GalleryState} state @param {number} [delay] */
function scheduleInlineProgressRelease(state, delay = SCROLL_SETTLE_MS + 90) {
  if (state.progressFollowTimer != null) window.clearTimeout(state.progressFollowTimer);
  state.progressFollowTimer = window.setTimeout(() => {
    state.progressFollowTimer = null;
    if (!state.gallery.isConnected || state.context !== 'inline') return;

    if (
      state.edgePointer ||
      state.slideshow.hasAttribute('dragging') ||
      state.slideshow.hasAttribute('transitioning')
    ) {
      scheduleInlineProgressRelease(state, SCROLL_SETTLE_MS + 60);
      return;
    }

    setInlineProgressFollowing(state, false);
    setProgressVisual(state, interpolatedInlineProgressPosition(state), 1);
  }, delay);
}

/** @param {GalleryState} state */
function beginInlineProgressFollowing(state) {
  if (state.context !== 'inline' || getAvailableIndexes(state).length <= 1) return;

  // Capture Horizon's current physical DOM order once at gesture/navigation
  // start. This is especially important for its temporary first↔last reorder.
  updateInlineGeometry(state);
  setInlineProgressFollowing(state, true);
  // Covers the interval between Horizon's select event and the first actual
  // scroll frame. Scroll events replace this generous guard with the short
  // settled release below.
  scheduleInlineProgressRelease(state, 650);
}

/** @param {GalleryState} state */
function renderInlineProgressFromScroll(state) {
  if (state.context !== 'inline' || getAvailableIndexes(state).length <= 1) return;

  setInlineProgressFollowing(state, true);
  setProgressVisual(state, interpolatedInlineProgressPosition(state), 1);
  scheduleInlineProgressRelease(state);
}

/** @param {GalleryState} state */
function scheduleInlineProgress(state) {
  if (state.progressFrame != null) return;
  state.progressFrame = requestAnimationFrame(() => {
    state.progressFrame = null;
    renderInlineProgressFromScroll(state);
  });
}

/** @param {GalleryState} state @param {number} position @param {number} [opacity] */
function setProgressVisual(state, position, opacity = 1) {
  const progress = ensureProgressElements(state);
  if (!progress) return;
  if (state.progressTrackWidth <= 1) {
    updateProgressMetrics(state);
  }
  progress.style.transform = progressTransform(state, position);
  progress.style.opacity = `${clampNumber(opacity, 0, 1)}`;
}

/** @param {GalleryState} state @param {number} index @param {boolean} announce */
function updateFooter(state, index, announce = false) {
  const indexes = getAvailableIndexes(state);
  const position = Math.max(0, indexes.indexOf(index));
  const total = indexes.length;
  const footer = state.gallery.querySelector('[data-aw-gallery-footer]') || createFooter(state.gallery);
  footer.hidden = total <= 1;
  ensureProgressElements(state);
  if (state.progressTrackWidth <= 1) updateProgressMetrics(state);

  const discretePosition = mappedProgressPosition(position, total);
  const inlineCarousel = state.context === 'inline' && total > 1;
  const preserveLivePosition = inlineCarousel && (
    inlineProgressIsFollowing(state) ||
    state.edgePointer ||
    state.slideshow.hasAttribute('transitioning') ||
    state.slideshow.hasAttribute('dragging')
  );

  if (preserveLivePosition) {
    setInlineProgressFollowing(state, true);
    setProgressVisual(state, interpolatedInlineProgressPosition(state), 1);
    scheduleInlineProgressRelease(state);
  } else {
    if (state.progressFollowTimer != null) {
      window.clearTimeout(state.progressFollowTimer);
      state.progressFollowTimer = null;
    }
    setInlineProgressFollowing(state, false);
    setProgressVisual(state, discretePosition, 1);
  }

  const live = footer.querySelector('[data-aw-live]');
  if (announce && live) {
    live.textContent = localeText().status
      .replace('{current}', String(position + 1))
      .replace('{total}', String(total));
  }
}

/** @param {GalleryState} state @param {number} [duration] */
function lockNavigation(state, duration = NAVIGATION_LOCK_MS) {
  state.lockUntil = Math.max(state.lockUntil, performance.now() + duration);
  state.gallery.setAttribute('data-aw-navigation-locked', '');
  if (state.lockTimer != null) window.clearTimeout(state.lockTimer);
  state.lockTimer = window.setTimeout(() => {
    state.lockTimer = null;
    if (performance.now() >= state.lockUntil) state.gallery.removeAttribute('data-aw-navigation-locked');
  }, duration + 30);
}

/** @param {GalleryState} state */
function navigationLocked(state) {
  return performance.now() < state.lockUntil;
}

/**
 * @param {GalleryState} state
 * @param {number} index
 * @param {{context?:'inline'|'fullscreen',userInitiated?:boolean,announce?:boolean}} [options]
 */
async function activate(state, index, options = {}) {
  const context = options.context || (state.context === 'fullscreen' ? 'fullscreen' : 'inline');
  const normalized = normalizeIndex(state, Number(index) || 0);
  const keepManualPause = isManuallyPaused(state, normalized);
  if (state.manualPause && !keepManualPause) state.manualPause = null;

  state.activeIndex = normalized;
  state.context = context;
  const token = ++state.activationToken;
  updateFooter(state, normalized, Boolean(options.announce));
  if (context === 'inline') updateReadyNeighbourhood(state, normalized);

  const item = contextItem(state, normalized, context);
  pauseAllExcept(state, item instanceof HTMLElement ? item : null);

  if (!(item instanceof HTMLElement) || !isVideoItem(item)) {
    refreshVideoUi(state);
    return;
  }

  // Repeated selection, viewport and section-morph events are idempotent. An
  // explicit pause remains authoritative for this visit, while leaving the
  // medium clears it so the next encounter autoplays as requested.
  if (keepManualPause) {
    pauseItem(state, item, { hideControls: false, forceMuted: false, remember: true });
    refreshVideoUi(state);
    return;
  }

  if (!playbackSurfaceIsVisible(state)) {
    pauseItem(state, item, { hideControls: false, forceMuted: true, remember: true });
    refreshVideoUi(state);
    return;
  }

  const video = nativeVideo(item);
  const root = videoRoot(item);
  const alreadyPlaying = video instanceof HTMLVideoElement
    ? !video.paused && !video.ended
    : root?.dataset.awVideoPlaying === 'true';
  if ((alreadyPlaying && playbackIsIntended(state, item)) || playbackIsPending(state, item)) {
    refreshVideoUi(state);
    return;
  }

  await playItem(state, item, { userInitiated: Boolean(options.userInitiated) });
  // playItem owns command arbitration. A stale activate() can only refresh UI;
  // it may never pause or restart a newer command.
  if (token !== state.activationToken || state.activeIndex !== normalized || state.context !== context) {
    refreshVideoUi(state);
  }
}

/** @param {GalleryState} state */
function refreshVideoUi(state) {
  for (const item of videoItems(state)) {
    const video = nativeVideo(item);
    if (video instanceof HTMLVideoElement) bindNativeVideo(state, item, video);
    const iframe = externalVideo(item);
    if (iframe instanceof HTMLIFrameElement) bindExternalVideo(state, item, iframe);
    const active = itemIsActive(state, item);
    const loaded = Boolean(video || iframe);
    const root = videoRoot(item);
    const pending = playbackIsPending(state, item);
    const playing = video instanceof HTMLVideoElement
      ? !video.paused && !video.ended
      : playbackIsIntended(state, item) && root?.dataset.awVideoPlaying === 'true';
    const audible = video instanceof HTMLVideoElement
      ? !video.muted && video.volume > 0
      : root?.dataset.awVideoAudible === 'true';
    setVideoUi(state, item, { active, loaded, playing, audible, pending });
  }
}

/** @param {GalleryState} state @param {-1|1} direction @param {Event} event */
function navigateInline(state, direction, event) {
  if (navigationLocked(state)) return false;
  const indexes = getAvailableIndexes(state);
  if (indexes.length <= 1) return false;

  const currentPosition = Math.max(0, indexes.indexOf(state.activeIndex));
  const targetIndex = indexes[(currentPosition + direction + indexes.length) % indexes.length];
  const targetSlide = state.slideByIndex.get(targetIndex) || null;
  if (!(targetSlide instanceof HTMLElement)) return false;

  lockNavigation(state);
  const slideId = targetSlide.getAttribute('slide-id');
  const synthetic = new Event(event.type || 'antlitzwerk:navigate', { bubbles: true, cancelable: true });

  if (typeof state.slideshow.select === 'function') {
    const componentSlides = Array.from(state.slideshow.slides || state.slideshow.refs?.slides || []);
    const atFirst = currentPosition === 0;
    const atLast = currentPosition === indexes.length - 1;
    const cyclicInput = direction < 0 && atFirst
      ? -1
      : direction > 0 && atLast
        ? componentSlides.length
        : null;

    if (cyclicInput != null) state.slideshow.select(cyclicInput, synthetic, { animate: true });
    else if (slideId) state.slideshow.select({ id: slideId }, synthetic, { animate: true });
    else state.slideshow.select(componentSlides.indexOf(targetSlide), synthetic, { animate: true });
  } else {
    targetSlide.scrollIntoView({ behavior: 'smooth', inline: 'start', block: 'nearest' });
    void activate(state, targetIndex, { context: 'inline', userInitiated: true, announce: true });
  }
  return true;
}

/** @param {number} value @param {number} minimum @param {number} maximum */
function clampNumber(value, minimum, maximum) {
  return Math.min(maximum, Math.max(minimum, value));
}

/** @param {number} value */
function snapToDevicePixel(value) {
  const ratio = Math.max(1, Math.min(4, Number(window.devicePixelRatio) || 1));
  return Math.round(value * ratio) / ratio;
}

/**
 * Keeps the active slide and both cyclic neighbours laid out so typography
 * is already painted even at the first↔last loop edge. No image is forced
 * to eager-load, decoded early or promoted to a permanent GPU layer.
 * @param {GalleryState} state
 * @param {number} index
 */
function updateReadyNeighbourhood(state, index) {
  const indexes = getAvailableIndexes(state);
  if (!indexes.length) return;
  const position = Math.max(0, indexes.indexOf(normalizeIndex(state, index)));
  const ready = new Set();

  for (let offset = -RENDER_NEIGHBOUR_RADIUS; offset <= RENDER_NEIGHBOUR_RADIUS; offset += 1) {
    const targetPosition = (position + offset + indexes.length) % indexes.length;
    const slide = state.slideByIndex.get(indexes[targetPosition]);
    if (slide instanceof HTMLElement) ready.add(slide);
  }

  for (const slide of [...state.readySlides]) {
    if (ready.has(slide)) continue;
    slide.classList.remove('is-aw-render-ready');
    state.readySlides.delete(slide);
  }

  for (const slide of ready) {
    slide.classList.add('is-aw-render-ready');
    state.readySlides.add(slide);
  }
}

/** @param {GalleryState} state @param {-1|1} direction */
function selectCyclicEdge(state, direction) {
  if (state.context !== 'inline' || typeof state.slideshow.select !== 'function') return false;
  const slides = state.slideshow.slides || state.slideshow.refs?.slides || [];
  const available = Array.from(slides).filter((slide) => !slide.hasAttribute('hidden') || slide.hasAttribute('reveal'));
  if (available.length <= 1) return false;

  if (state.lockTimer != null) window.clearTimeout(state.lockTimer);
  state.lockUntil = 0;
  const requestedIndex = direction < 0 ? -1 : available.length;
  const synthetic = new Event('antlitzwerk:edge-swipe', { bubbles: true, cancelable: true });
  state.suppressClickUntil = performance.now() + CLICK_SUPPRESSION_MS;
  void state.slideshow.select(requestedIndex, synthetic, { animate: true });
  return true;
}

/**
 * Starts a gesture record without preventing the browser's own scrolling.
 * @param {GalleryState} state
 * @param {'pointer'|'touch'} source
 * @param {number} id
 * @param {number} x
 * @param {number} y
 * @param {EventTarget | null} eventTarget
 */
function beginEdgeGesture(state, source, id, x, y, eventTarget) {
  const target = eventTarget instanceof Element ? eventTarget : null;
  if (!target || state.context !== 'inline') return;
  if (target.closest(PROTECTED_CONTROL_SELECTOR) || target.closest('model-viewer')) return;

  const indexes = getAvailableIndexes(state);
  if (indexes.length <= 1) return;
  const startIndex = normalizeIndex(state, currentInlineIndex(state));
  const startPosition = indexes.indexOf(startIndex);
  if (startPosition < 0) return;

  state.edgePointer = {
    source,
    id,
    startX: x,
    startY: y,
    lastX: x,
    lastY: y,
    startPosition,
    cancelled: false,
  };
  beginInlineProgressFollowing(state);
}

/** @param {GalleryState} state @param {'pointer'|'touch'} source @param {number} id @param {number} x @param {number} y */
function updateEdgeGesture(state, source, id, x, y) {
  const gesture = state.edgePointer;
  if (!gesture || gesture.source !== source || gesture.id !== id) return;
  gesture.lastX = x;
  gesture.lastY = y;

  const dx = gesture.lastX - gesture.startX;
  const dy = gesture.lastY - gesture.startY;
  if (Math.abs(dy) > Math.abs(dx) * 1.25 && Math.abs(dy) > POINTER_INTENT_DISTANCE) {
    gesture.cancelled = true;
  }
}

/**
 * Completes only an outward gesture that remained at a physical track edge.
 * Normal swipes have already been handled natively and return without work.
 * @param {GalleryState} state
 * @param {'pointer'|'touch'} source
 * @param {number} id
 * @param {number} x
 * @param {number} y
 * @param {boolean} [cancelOnly]
 */
function finishEdgeGesture(state, source, id, x, y, cancelOnly = false) {
  const gesture = state.edgePointer;
  if (!gesture || gesture.source !== source || gesture.id !== id) return;
  state.edgePointer = null;

  gesture.lastX = Number.isFinite(x) ? x : gesture.lastX;
  gesture.lastY = Number.isFinite(y) ? y : gesture.lastY;
  const dx = gesture.lastX - gesture.startX;
  const dy = gesture.lastY - gesture.startY;
  const absX = Math.abs(dx);
  const absY = Math.abs(dy);

  // Horizon suppresses the click generated by desktop dragging at document
  // level. Our zoom/media actions run in window capture, so remember the same
  // horizontal intent here before that click can arrive.
  if (absX > POINTER_INTENT_DISTANCE && absX > absY * 1.05) {
    state.suppressClickUntil = performance.now() + CLICK_SUPPRESSION_MS;
  }

  if (cancelOnly || gesture.cancelled || absX < EDGE_SWIPE_MIN_DISTANCE || absX <= absY * EDGE_SWIPE_DOMINANCE) return;

  const indexes = getAvailableIndexes(state);
  if (indexes.length <= 1) return;
  const direction = dx < 0 ? 1 : -1;
  const currentScrollLeft = state.scroller.scrollLeft;
  const maxScrollLeft = Math.max(0, state.scroller.scrollWidth - state.scroller.clientWidth);
  const stayedAtStart = gesture.startPosition === 0 && currentScrollLeft <= EDGE_SCROLL_TOLERANCE;
  const stayedAtEnd = gesture.startPosition === indexes.length - 1 && maxScrollLeft - currentScrollLeft <= EDGE_SCROLL_TOLERANCE;
  if (!((direction < 0 && stayedAtStart) || (direction > 0 && stayedAtEnd))) return;

  // Run after the browser/Horizon has finished its own release bookkeeping.
  requestAnimationFrame(() => {
    if (state.gallery.isConnected) selectCyclicEdge(state, direction);
  });
}

/** @param {GalleryState} state @param {PointerEvent} event */
function handleNativePointerDown(state, event) {
  // Touch is tracked with Touch Events because browsers correctly cancel
  // Pointer Events as soon as asynchronous native panning takes ownership.
  if (!event.isPrimary || event.button !== 0 || event.pointerType === 'touch') return;
  beginEdgeGesture(state, 'pointer', event.pointerId, event.clientX, event.clientY, event.target);
}

/** @param {GalleryState} state @param {PointerEvent} event */
function handleNativePointerMove(state, event) {
  updateEdgeGesture(state, 'pointer', event.pointerId, event.clientX, event.clientY);
}

/** @param {GalleryState} state @param {PointerEvent} event @param {boolean} [cancelOnly] */
function handleNativePointerEnd(state, event, cancelOnly = false) {
  finishEdgeGesture(
    state,
    'pointer',
    event.pointerId,
    event.clientX,
    event.clientY,
    cancelOnly
  );
}

/** @param {TouchList} touches @param {number} identifier */
function touchByIdentifier(touches, identifier) {
  for (const touch of Array.from(touches || [])) {
    if (touch.identifier === identifier) return touch;
  }
  return null;
}

/** @param {GalleryState} state @param {TouchEvent} event */
function handleNativeTouchStart(state, event) {
  if (event.touches.length !== 1) {
    state.edgePointer = null;
    return;
  }
  const touch = event.touches[0];
  beginEdgeGesture(state, 'touch', touch.identifier, touch.clientX, touch.clientY, event.target);
}

/** @param {GalleryState} state @param {TouchEvent} event */
function handleNativeTouchMove(state, event) {
  const gesture = state.edgePointer;
  if (!gesture || gesture.source !== 'touch') return;
  const touch = touchByIdentifier(event.touches, gesture.id);
  if (touch) updateEdgeGesture(state, 'touch', gesture.id, touch.clientX, touch.clientY);
}

/** @param {GalleryState} state @param {TouchEvent} event @param {boolean} [cancelOnly] */
function handleNativeTouchEnd(state, event, cancelOnly = false) {
  const gesture = state.edgePointer;
  if (!gesture || gesture.source !== 'touch') return;
  const touch = touchByIdentifier(event.changedTouches, gesture.id);
  finishEdgeGesture(
    state,
    'touch',
    gesture.id,
    touch?.clientX ?? gesture.lastX,
    touch?.clientY ?? gesture.lastY,
    cancelOnly
  );
}

/** @param {GalleryState} state @param {WheelEvent} event */
function handleWheel(state, event) {
  if (event.ctrlKey) return;
  const target = event.target instanceof Element ? event.target : null;
  if (target?.closest(PROTECTED_CONTROL_SELECTOR) || target?.closest('model-viewer')) return;

  const horizontalDelta = event.shiftKey && Math.abs(event.deltaY) > Math.abs(event.deltaX)
    ? event.deltaY
    : event.deltaX;
  const verticalDelta = event.shiftKey ? 0 : event.deltaY;
  if (Math.abs(horizontalDelta) <= Math.abs(verticalDelta) * 1.05) return;

  event.preventDefault();
  if (state.wheelTimer != null) window.clearTimeout(state.wheelTimer);
  state.wheelTimer = window.setTimeout(() => {
    state.wheelTimer = null;
    state.wheelTotal = 0;
    state.wheelLatched = false;
  }, WHEEL_IDLE_MS);

  // Keep the whole physical trackpad/mouse-wheel burst latched while a prior
  // navigation is still animating. Otherwise the momentum tail can arrive
  // after the transition lock expires and incorrectly trigger a second item.
  if (navigationLocked(state)) {
    state.wheelLatched = true;
    return;
  }

  if (state.wheelLatched) return;
  state.wheelTotal += horizontalDelta;
  if (Math.abs(state.wheelTotal) < WHEEL_THRESHOLD) return;

  state.wheelLatched = true;
  navigateInline(state, state.wheelTotal > 0 ? 1 : -1, event);
}

/** @param {GalleryState} state @param {KeyboardEvent} event */
function handleKeyDown(state, event) {
  if (event.defaultPrevented || event.altKey || event.ctrlKey || event.metaKey || state.context !== 'inline') return;
  const target = event.target instanceof Element ? event.target : null;

  if ((event.key === 'Enter' || event.key === ' ') && target?.matches('slideshow-slide.product-media-container--zoomable')) {
    event.preventDefault();
    const index = mediaIndex(target);
    state.zoomDialog?.open?.(index, event);
    return;
  }

  if (
    target?.closest('button, a, input, select, textarea, video, model-viewer, [contenteditable="true"]')
  ) return;

  if (event.key === 'ArrowLeft' || event.key === 'ArrowRight') {
    event.preventDefault();
    navigateInline(state, event.key === 'ArrowRight' ? 1 : -1, event);
  }
}

/** @param {GalleryState} state @param {Event} event */
function handleWindowClick(state, event) {
  const target = event.target instanceof Element ? event.target : null;
  if (!target || !state.gallery.contains(target)) return;

  const zoomSuppresses = typeof state.zoomDialog?.isClickSuppressed === 'function' && state.zoomDialog.isClickSuppressed();
  const localSuppresses = performance.now() < state.suppressClickUntil;
  if ((zoomSuppresses || localSuppresses) && target.closest(VIDEO_ROOT_SELECTOR + ', slideshow-slide, .media-gallery__grid')) {
    stopEvent(event);
    return;
  }

  const soundButton = target.closest(SOUND_SELECTOR);
  if (soundButton instanceof HTMLButtonElement) {
    const item = soundButton.closest(VIDEO_ITEM_SELECTOR);
    if (item instanceof HTMLElement) toggleSound(state, item, event);
    else stopEvent(event);
    return;
  }

  const expandButton = target.closest(EXPAND_SELECTOR);
  if (expandButton instanceof HTMLButtonElement) {
    stopEvent(event);
    const index = mediaIndex(expandButton);
    if (!state.zoomDialog) return;
    state.zoomDialog.open?.(index, event);
    return;
  }

  const videoItem = target.closest(VIDEO_ITEM_SELECTOR);
  if (videoItem instanceof HTMLElement && isVideoItem(videoItem)) {
    const playTarget = target.closest(`${PLAY_SELECTOR}, .deferred-media__poster-button, video`);
    if (playTarget) {
      void togglePlayback(state, videoItem, event);
      return;
    }
  }

  // Image zoom is handled here at window-capture level so a drag-generated
  // click can never reach Horizon's document-level declarative listener first.
  const gridZoomButton = target.closest('.product-media-container__zoom-button');
  const zoomableSlide = target.closest('slideshow-slide.product-media-container--zoomable[data-aw-media-index]');
  if ((gridZoomButton || zoomableSlide) && !target.closest('zoom-dialog') && state.zoomDialog) {
    stopEvent(event);
    const source = gridZoomButton?.closest('[data-aw-media-index]') || zoomableSlide;
    state.zoomDialog.open?.(mediaIndex(source), event);
    return;
  }

  // Gate Horizon arrows/thumbnails before its document-capture event handler.
  const nativeButton = target.closest('slideshow-component button[on\\:click]');
  if (nativeButton && state.slideshow.contains(nativeButton)) {
    const action = nativeButton.getAttribute('on:click') || '';
    const navigates = action === '/next' || action === '/previous' || action.startsWith('/select/');
    if (!navigates) return;
    if (navigationLocked(state)) {
      stopEvent(event);
      return;
    }

    // A thumbnail or dot may jump several positions. Prepare its semantic
    // media before Horizon reads the offset and begins scrolling, without
    // changing image loading, fetch priority, source set or quality.
    if (action.startsWith('/select/')) {
      const targetPosition = Number.parseInt(action.slice('/select/'.length), 10);
      const componentSlides = Array.from(state.slideshow.slides || state.slideshow.refs?.slides || []);
      const targetSlide = componentSlides[targetPosition];
      if (targetSlide instanceof HTMLElement) updateReadyNeighbourhood(state, mediaIndex(targetSlide));
    }

    lockNavigation(state);
  }
}

/** @param {GalleryState} state @param {CustomEvent} event */
function handleSlideshowSelect(state, event) {
  const slide = event.detail?.slide instanceof Element
    ? event.detail.slide
    : state.slideshow.querySelector(`slideshow-slide[data-aw-media-index="${currentInlineIndex(state)}"]`);
  const index = mediaIndex(slide);

  if (state.context === 'inline') beginInlineProgressFollowing(state);
  if (event.detail?.userInitiated) lockNavigation(state);
  if (state.context === 'fullscreen' || state.context === 'closing') {
    state.activeIndex = normalizeIndex(state, index);
    updateFooter(state, state.activeIndex, false);
    refreshVideoUi(state);
    return;
  }

  void activate(state, index, {
    context: 'inline',
    userInitiated: Boolean(event.detail?.userInitiated),
    announce: Boolean(event.detail?.userInitiated),
  });
}

/** @param {GalleryState} state @param {CustomEvent} event */
function handleZoomOpen(state, event) {
  const index = normalizeIndex(state, Number(event.detail?.index) || 0);
  const source = inlineItem(state, index);
  if (source instanceof HTMLElement && isVideoItem(source)) {
    pauseItem(state, source, { hideControls: true, forceMuted: true, remember: true });
  } else {
    pauseAllExcept(state, null);
  }
  state.context = 'fullscreen';
  state.activeIndex = index;
  updateFooter(state, index, false);
}

/** @param {GalleryState} state @param {CustomEvent} event */
function handleZoomSelected(state, event) {
  const index = normalizeIndex(state, Number(event.detail?.index) || 0);
  void activate(state, index, {
    context: 'fullscreen',
    userInitiated: true,
    announce: false,
  });
}

/** @param {GalleryState} state @param {CustomEvent} event */
function handleZoomClosing(state, event) {
  const index = normalizeIndex(state, Number(event.detail?.index) || state.activeIndex);
  const item = fullscreenItem(state, index);
  if (item instanceof HTMLElement && isVideoItem(item)) {
    pauseItem(state, item, { hideControls: true, forceMuted: true, remember: true });
  } else {
    pauseAllExcept(state, null);
  }
  state.activeIndex = index;
  state.context = 'closing';
}

/** @param {GalleryState} state @param {CustomEvent} event */
function handleZoomClose(state, event) {
  const index = normalizeIndex(state, Number(event.detail?.index) || state.activeIndex);
  state.context = 'inline';
  void activate(state, index, {
    context: 'inline',
    userInitiated: true,
    announce: false,
  });
}

/** @param {GalleryState} state */
function refresh(state) {
  if (!state.gallery.isConnected) {
    destroyGallery(state);
    return;
  }

  const slideshow = state.gallery.querySelector('slideshow-component[data-aw-product-slideshow]');
  const scroller = slideshow?.querySelector('slideshow-slides');
  const zoomDialog = state.gallery.querySelector('zoom-dialog[data-aw-zoom-dialog]');
  if (slideshow !== state.slideshow || scroller !== state.scroller || zoomDialog !== state.zoomDialog) {
    const gallery = state.gallery;
    destroyGallery(state);
    initializeGallery(gallery);
    return;
  }

  refreshInlineCache(state);
  scheduleInlineGeometry(state);

  const index = normalizeIndex(state, state.context === 'inline' ? currentInlineIndex(state) : state.activeIndex);
  state.activeIndex = index;
  updateReadyNeighbourhood(state, index);
  updateFooter(state, index, false);
  validateGalleryContract(state);
  void activate(state, index, {
    context: state.context === 'fullscreen' ? 'fullscreen' : 'inline',
    userInitiated: false,
    announce: false,
  });
}

/** @param {GalleryState} state */
function destroyGallery(state) {
  if (!activeStates.has(state)) return;
  state.controller.abort();
  state.observer.disconnect();
  if (state.mutationFrame) cancelAnimationFrame(state.mutationFrame);
  if (state.lockTimer != null) window.clearTimeout(state.lockTimer);
  if (state.wheelTimer != null) window.clearTimeout(state.wheelTimer);
  if (state.resizeFrame != null) cancelAnimationFrame(state.resizeFrame);
  if (state.geometryFrame != null) cancelAnimationFrame(state.geometryFrame);
  if (state.progressFrame != null) cancelAnimationFrame(state.progressFrame);
  if (state.visibilityFrame != null) cancelAnimationFrame(state.visibilityFrame);
  if (state.scrollSettleTimer != null) window.clearTimeout(state.scrollSettleTimer);
  if (state.progressFollowTimer != null) window.clearTimeout(state.progressFollowTimer);
  state.viewportObserver?.disconnect();
  state.sizeObserver?.disconnect();
  for (const slide of state.readySlides) slide.classList.remove('is-aw-render-ready');
  state.readySlides.clear();
  pauseAllExcept(state, null);
  activeStates.delete(state);
  galleryStates.delete(state.gallery);
  delete state.gallery.dataset.antlitzwerkProductGallery;
  state.gallery.classList.remove('aw-product-gallery--ready');
  state.gallery.removeAttribute('data-aw-navigation-locked');
  state.gallery.removeAttribute('data-aw-gallery-visible');
  state.gallery.removeAttribute('data-aw-gallery-contract-status');
}

/** @param {HTMLElement} gallery */
function initializeGallery(gallery) {
  if (!(gallery instanceof HTMLElement) || galleryStates.has(gallery)) return;
  const slideshow = gallery.querySelector('slideshow-component[data-aw-product-slideshow]');
  const scroller = slideshow?.querySelector('slideshow-slides');
  if (!(slideshow instanceof HTMLElement) || !(scroller instanceof HTMLElement)) return;

  const controller = new AbortController();
  const { signal } = controller;
  const active = getInitialIndex(slideshow);
  /** @type {GalleryState} */
  const state = {
    gallery,
    slideshow,
    scroller,
    zoomDialog: gallery.querySelector('zoom-dialog[data-aw-zoom-dialog]'),
    controller,
    observer: new MutationObserver(() => {}),
    activeIndex: active,
    context: 'inline',
    activationToken: 0,
    mutationFrame: 0,
    suppressClickUntil: 0,
    lockUntil: 0,
    lockTimer: null,
    edgePointer: null,
    wheelTotal: 0,
    wheelLatched: false,
    wheelTimer: null,
    boundVideos: new WeakSet(),
    boundExternalVideos: new WeakSet(),
    videoMemory: new Map(),
    playbackSerial: 0,
    playbackTokens: new WeakMap(),
    playbackIntent: new WeakMap(),
    manualPause: null,
    inlineSlides: [],
    inlineIndexes: [],
    slideByIndex: new Map(),
    inlineProgressGeometry: [],
    inlineViewportWidth: 0,
    geometryFrame: null,
    sizeObserver: null,
    readySlides: new Set(),
    progress: null,
    progressTrack: null,
    progressTrackWidth: 0,
    resizeFrame: null,
    progressFrame: null,
    progressFollowTimer: null,
    pageVisible: !document.hidden,
    galleryVisible: false,
    viewportObserver: null,
    visibilityFrame: null,
    scrollSettleTimer: null,
    pendingPlayback: new WeakMap(),
    timeMemoryAt: new WeakMap(),
    contractWarningIssued: false,
  };

  refreshInlineCache(state);
  galleryStates.set(gallery, state);
  activeStates.add(state);
  gallery.dataset.antlitzwerkProductGallery = '';
  gallery.classList.add('aw-product-gallery--ready');

  slideshow.addEventListener('slideshow:select', (event) => handleSlideshowSelect(state, event), { signal });
  // Horizon remains authoritative, while this settled-geometry fallback covers
  // direct browser scroll-snap paths on mobile and precision touchpads.
  scroller.addEventListener('scroll', () => {
    scheduleInlineProgress(state);
    scheduleScrollSelection(state, false);
  }, { passive: true, signal });
  if ('onscrollend' in scroller) {
    scroller.addEventListener('scrollend', () => scheduleScrollSelection(state, true), { passive: true, signal });
  }
  // Ordinary horizontal movement remains fully native. These passive listeners
  // only recognise an outward swipe at the two cyclic edges.
  scroller.addEventListener('pointerdown', (event) => handleNativePointerDown(state, event), {
    capture: true,
    passive: true,
    signal,
  });
  window.addEventListener('pointermove', (event) => handleNativePointerMove(state, event), {
    passive: true,
    signal,
  });
  window.addEventListener('pointerup', (event) => handleNativePointerEnd(state, event), {
    passive: true,
    signal,
  });
  window.addEventListener('pointercancel', (event) => handleNativePointerEnd(state, event, true), {
    passive: true,
    signal,
  });
  scroller.addEventListener('touchstart', (event) => handleNativeTouchStart(state, event), {
    capture: true,
    passive: true,
    signal,
  });
  window.addEventListener('touchmove', (event) => handleNativeTouchMove(state, event), {
    passive: true,
    signal,
  });
  window.addEventListener('touchend', (event) => handleNativeTouchEnd(state, event), {
    passive: true,
    signal,
  });
  window.addEventListener('touchcancel', (event) => handleNativeTouchEnd(state, event, true), {
    passive: true,
    signal,
  });
  scroller.addEventListener('wheel', (event) => handleWheel(state, event), { passive: false, signal });
  gallery.addEventListener('keydown', (event) => handleKeyDown(state, event), { capture: true, signal });
  gallery.addEventListener('dragstart', (event) => {
    const target = event.target instanceof Element ? event.target : null;
    if (target?.closest('.product-media img, slideshow-slide img')) event.preventDefault();
  }, { capture: true, signal });

  // Click capture runs before Horizon's document-level declarative events.
  // Pointerdown stays native; protected controls are excluded by data-aw-control.
  window.addEventListener('click', (event) => handleWindowClick(state, event), { capture: true, signal });

  window.addEventListener('resize', () => {
    if (state.resizeFrame != null) return;
    state.resizeFrame = requestAnimationFrame(() => {
      state.resizeFrame = null;
      if (!state.gallery.isConnected) return;
      updateInlineGeometry(state);
      updateFooter(state, state.activeIndex, false);
      updateReadyNeighbourhood(state, state.activeIndex);
    });
  }, { passive: true, signal });

  if (state.zoomDialog) {
    state.zoomDialog.addEventListener('antlitzwerk:zoom-open', (event) => handleZoomOpen(state, event), { signal });
    state.zoomDialog.addEventListener('antlitzwerk:zoom-selected', (event) => handleZoomSelected(state, event), { signal });
    state.zoomDialog.addEventListener('antlitzwerk:zoom-closing', (event) => handleZoomClosing(state, event), { signal });
    state.zoomDialog.addEventListener('antlitzwerk:zoom-close', (event) => handleZoomClose(state, event), { signal });
  }

  document.addEventListener('visibilitychange', () => {
    state.pageVisible = !document.hidden;
    if (!state.pageVisible) {
      suspendActivePlayback(state);
      return;
    }
    if (!state.gallery.isConnected || state.context === 'closing') return;
    if (state.context === 'inline') setGalleryVisibility(state, elementIsMeaningfullyVisible(state.gallery));
    void activate(state, state.activeIndex, {
      context: state.context === 'fullscreen' ? 'fullscreen' : 'inline',
      userInitiated: false,
      announce: false,
    });
  }, { signal });

  window.addEventListener('pageshow', () => {
    state.pageVisible = true;
    if (state.context === 'inline') setGalleryVisibility(state, elementIsMeaningfullyVisible(state.gallery));
    if (state.gallery.isConnected && state.context !== 'closing') {
      void activate(state, state.activeIndex, {
        context: state.context === 'fullscreen' ? 'fullscreen' : 'inline',
        userInitiated: false,
        announce: false,
      });
    }
  }, { signal });

  const observer = new MutationObserver((records) => {
    const relevant = records.some((record) => {
      if (record.type === 'childList') {
        const target = record.target;
        if (
          target instanceof Element &&
          target.matches('deferred-media, slideshow-slides, slideshow-component, zoom-dialog, .dialog-zoomed-gallery')
        ) {
          return true;
        }

        return [...record.addedNodes, ...record.removedNodes].some((node) =>
          node instanceof Element && (
            node.matches('slideshow-slide, deferred-media, slideshow-slides, slideshow-component, zoom-dialog') ||
            node.querySelector?.('slideshow-slide, deferred-media, slideshow-slides, slideshow-component, zoom-dialog')
          )
        );
      }
      const target = record.target;
      return target instanceof Element && target.matches(
        'slideshow-slide, deferred-media, slideshow-slides, slideshow-component, zoom-dialog, .dialog-zoomed-gallery > li'
      );
    });

    if (!relevant || state.mutationFrame) return;
    state.inlineSlides = [];
    state.inlineIndexes = [];
    state.slideByIndex.clear();
    state.mutationFrame = requestAnimationFrame(() => {
      state.mutationFrame = 0;
      refresh(state);
    });
  });
  observer.observe(gallery, {
    subtree: true,
    childList: true,
    attributes: true,
    attributeFilter: [
      'hidden',
      'reveal',
      'data-media-loaded',
      'data-aw-media-index',
      'data-aw-media-id',
      'slide-id',
    ],
  });
  state.observer = observer;

  installViewportObserver(state);
  validateGalleryContract(state);
  updateInlineGeometry(state);
  if ('ResizeObserver' in window) {
    state.sizeObserver = new ResizeObserver(() => scheduleInlineGeometry(state));
    state.sizeObserver.observe(state.scroller);
    if (state.progressTrack instanceof HTMLElement) state.sizeObserver.observe(state.progressTrack);
  }
  updateReadyNeighbourhood(state, state.activeIndex);
  updateFooter(state, state.activeIndex, false);
  if (document.fonts?.ready) {
    void document.fonts.ready.then(() => {
      if (state.gallery.isConnected) {
        updateReadyNeighbourhood(state, state.activeIndex);
        scheduleInlineGeometry(state);
      }
    });
  }
  void activate(state, state.activeIndex, { context: 'inline', userInitiated: false, announce: false });
}

/** @param {HTMLElement} slideshow */
function getInitialIndex(slideshow) {
  const visible = [...slideshow.querySelectorAll(INLINE_SLIDE_SELECTOR)]
    .find((slide) => slide.getAttribute('aria-hidden') !== 'true');
  return mediaIndex(visible || slideshow.querySelector(INLINE_SLIDE_SELECTOR));
}

/** @param {Document | Element} [root] */
function scan(root = document) {
  if (root instanceof Element && root.matches(GALLERY_SELECTOR)) initializeGallery(root);
  root.querySelectorAll?.(GALLERY_SELECTOR).forEach(initializeGallery);
}

function cleanupDisconnected() {
  for (const state of [...activeStates]) {
    if (!state.gallery.isConnected) destroyGallery(state);
  }
}

function start() {
  scan(document);

  const documentObserver = new MutationObserver((records) => {
    for (const record of records) {
      for (const node of record.addedNodes) {
        if (node instanceof Element) scan(node);
      }
    }
    cleanupDisconnected();
  });
  documentObserver.observe(document.documentElement, { childList: true, subtree: true });

  window.addEventListener('pagehide', (event) => {
    if (event.persisted) {
      for (const state of activeStates) pauseAllExcept(state, null);
      return;
    }

    documentObserver.disconnect();
    for (const state of [...activeStates]) destroyGallery(state);
  });
}

if (document.readyState === 'loading') {
  document.addEventListener('DOMContentLoaded', start, { once: true });
} else {
  start();
}
